/**
 * Aiming a ground move (ท่าเลือกพื้นที่) without a mouse (design docs/design/ui/2026-10-11-ground-aim/README.md, combat
 * spec docs/design/combat/2026-10-11-long-moves.md §3.3). The reticle itself is the renderer's `aim` (its slot, reach and
 * blast radius) drawn by render/passes/groundAim.ts; with a mouse it follows the pointer (input.ts), here a picked
 * ground point (`groundAim.at`) moves it instead:
 * - touch (ui/groundAimTouch.ts): drag from the move's button, a virtual stick from the player (distance ∝ drag, held to
 *   the reach); release casts, release back on the button cancels, a plain tap fires at the foe's feet as before;
 * - gamepad (gamepad.ts): hold the move's button, the reticle starts at the foe's feet, the right stick moves it; release
 *   casts, B cancels.
 */
import { screenDirToWorld, type Entity, type PlayerCommand, type World } from '@nine/shared';
import type { InputView } from './input';

interface Point {
  x: number;
  y: number;
}

/** The point a touch drag or the pad's stick has picked (tiles), or null: the mouse aims. */
export const groundAim: { at: Point | null } = { at: null };

interface Host {
  world: World;
  view: InputView;
  send: (c: PlayerCommand) => void;
}
let host: Host | null = null;

/** Called once by Input. */
export function bindGroundAim(h: Host): void {
  host = h;
}

/** The ground move in this hotbar slot, if it is one. */
export function groundMoveAt(slot: number): { range: number; radius: number } | null {
  const s = host?.world.activeSkills()[slot];
  return s?.ground ? { range: s.range, radius: s.aoe ?? 1 } : null;
}

/** A point held to the reach round the player. */
function clampToReach(x: number, y: number, range: number): Point {
  const p = host!.world.player;
  const dx = x - p.x;
  const dy = y - p.y;
  const d = Math.hypot(dx, dy);
  const k = d > range ? range / d : 1;
  return { x: p.x + dx * k, y: p.y + dy * k };
}

/** Where a reticle starts: the foe's feet (the target, else the nearest foe in reach), else 3 tiles ahead. */
function startPoint(range: number): Point {
  const w = host!.world;
  const p = w.player;
  const t = w.get(p.targetId);
  const foe: Entity | null = t && !t.dead && t !== p ? t : null;
  if (foe) return clampToReach(foe.x, foe.y, range);
  const f = Math.hypot(p.dirX, p.dirY) || 1;
  return clampToReach(p.x + (p.dirX / f) * Math.min(3, range), p.y + (p.dirY / f) * Math.min(3, range), range);
}

/** Starts aiming a slot's ground move at a picked point (touch or pad); false if the slot holds no ground move. */
export function aimBegin(slot: number): boolean {
  const m = groundMoveAt(slot);
  if (!m || !host) return false;
  host.view.aim = { slot, range: m.range, radius: m.radius };
  groundAim.at = startPoint(m.range);
  return true;
}

/** The map direction of a screen direction (x right, y down), at this camera angle. */
function mapDir(sx: number, sy: number): [number, number] {
  return host!.view.iso.dirToMap(...screenDirToWorld(sx, sy));
}

/**
 * Touch: the reticle at a drag from the player of (sx, sy) screen pixels, `full` pixels reaching the move's whole reach.
 */
export function aimDrag(sx: number, sy: number, full: number): void {
  const a = host?.view.aim;
  if (!a || !host) return;
  const p = host.world.player;
  const len = Math.hypot(sx, sy);
  if (len < 1) {
    groundAim.at = { x: p.x, y: p.y };
    return;
  }
  const [dx, dy] = mapDir(sx / len, sy / len);
  const n = Math.hypot(dx, dy) || 1;
  const d = Math.min(1, len / full) * a.range;
  groundAim.at = clampToReach(p.x + (dx / n) * d, p.y + (dy / n) * d, a.range);
}

/** Pad: moves the reticle by the right stick (screen axes, −1..1) for `dt` seconds. */
export function aimNudge(sx: number, sy: number, dt: number): void {
  const a = host?.view.aim;
  const at = groundAim.at;
  if (!a || !at || !host) return;
  const m = Math.hypot(sx, sy);
  if (m < 0.25) return;
  const [dx, dy] = mapDir(sx / m, sy / m);
  const n = Math.hypot(dx, dy) || 1;
  const speed = 7 * Math.min(1, (m - 0.25) / 0.75);
  groundAim.at = clampToReach(at.x + (dx / n) * speed * dt, at.y + (dy / n) * speed * dt, a.range);
}

/** Ends a touch / pad aim: cast at the picked point, or cancel. */
export function aimEnd(cast: boolean): void {
  const a = host?.view.aim;
  const at = groundAim.at;
  groundAim.at = null;
  if (!host || !a) return;
  host.view.aim = null;
  if (cast && at) host.send({ t: 'skill', slot: a.slot, at: { x: at.x, y: at.y } });
}

/** A plain tap on a ground move's button: cast with no point picked (the foe's feet, or ahead). */
export function aimTap(slot: number): void {
  if (!host) return;
  groundAim.at = null;
  host.view.aim = null;
  host.send({ t: 'skill', slot });
}

/** Whether a touch / pad aim is under way. */
export const aimingByPoint = (): boolean => !!host?.view.aim && !!groundAim.at;
